import React from 'react';
import {
  MapPin,
  CalendarDays,
  Trophy,
  Ticket,
  ExternalLink,
  ClipboardList,
} from 'lucide-react';
import {
  CalendarEvent,
  CalendarCategory,
} from '../../services/calendarService';
import EventThumbnail from './EventThumbnail';
import {
  ACCENT,
  dateBadgeParts,
  flagEmoji,
  formatDateRange,
  formatLocation,
  formatPrize,
  getEffectiveStatus,
} from './eventUtils';

interface EventTimelineCardProps {
  event: CalendarEvent;
  category?: CalendarCategory | null;
  onOpenSource?: (event: CalendarEvent) => void;
}

const StatusBadge: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  const status = getEffectiveStatus(event);
  if (status === 'ongoing') {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
        style={{ backgroundColor: `${ACCENT}1F`, color: ACCENT }}
      >
        <span className="relative flex w-2 h-2">
          <span
            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
            style={{ backgroundColor: ACCENT }}
          />
          <span
            className="relative inline-flex rounded-full w-2 h-2"
            style={{ backgroundColor: ACCENT }}
          />
        </span>
        Läuft
      </span>
    );
  }
  if (status === 'cancelled') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-500/15 text-red-400">
        Abgesagt
      </span>
    );
  }
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-zinc-800 text-gray-400">
        Beendet
      </span>
    );
  }
  return null;
};

const LinkButton: React.FC<{
  href: string;
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}> = ({ href, icon, label, primary = false, onClick }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
      primary
        ? 'text-black hover:opacity-90'
        : 'bg-zinc-800 text-gray-300 hover:bg-zinc-700 hover:text-white'
    }`}
    style={primary ? { backgroundColor: ACCENT } : undefined}
  >
    {icon}
    {label}
  </a>
);

/**
 * Einzelne Event-Karte in der Timeline: Thumbnail (mit Fallback),
 * Datum-Badge, Titel, Ort mit Flagge, Organisation, Preisgeld,
 * Status und externe Links.
 */
const EventTimelineCard: React.FC<EventTimelineCardProps> = ({
  event,
  category,
  onOpenSource,
}) => {
  const status = getEffectiveStatus(event);
  const badge = dateBadgeParts(event);
  const flag = flagEmoji(event.countryCode);
  const location = formatLocation(event);
  const prize = formatPrize(event);
  const isCancelled = status === 'cancelled';

  return (
    <article
      className={`rounded-2xl bg-zinc-900 border overflow-hidden transition-colors ${
        status === 'ongoing'
          ? 'border-[#3fced1]/40 shadow-[0_0_24px_-8px_rgba(63,206,209,0.35)]'
          : 'border-zinc-800 hover:border-zinc-700'
      } ${isCancelled ? 'opacity-70' : ''}`}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Thumbnail (16:9, passend zum Bildformat) */}
        <div className="relative w-full aspect-video sm:aspect-auto sm:w-64 lg:w-72 sm:h-auto sm:min-h-[150px] flex-shrink-0">
          <EventThumbnail
            event={event}
            category={category}
            className="absolute inset-0 w-full h-full"
          />
          {badge && (
            <div className="absolute top-2 left-2 rounded-xl bg-black/75 backdrop-blur-sm px-2 py-1 text-center min-w-[42px]">
              <div className="text-sm font-extrabold leading-tight" style={{ color: ACCENT }}>
                {badge.top}
              </div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-300 leading-tight">
                {badge.bottom}
              </div>
            </div>
          )}
        </div>

        {/* Inhalt */}
        <div className="flex-1 min-w-0 p-3.5 sm:p-4 flex flex-col gap-2">
          {/* Badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            {event.organization && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-zinc-800 text-gray-300 border border-zinc-700">
                {event.organization}
              </span>
            )}
            {category?.name && (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{
                  backgroundColor: `${category.color || ACCENT}1F`,
                  color: category.color || ACCENT,
                }}
              >
                {category.name}
              </span>
            )}
            <StatusBadge event={event} />
            {prize && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
              >
                <Trophy size={11} />
                {prize}
              </span>
            )}
          </div>

          {/* Titel */}
          <h3
            className={`font-bold text-white text-base sm:text-lg leading-snug line-clamp-2 ${
              isCancelled ? 'line-through decoration-red-400/70' : ''
            }`}
          >
            {event.title || 'Event'}
          </h3>

          {/* Meta */}
          <div className="flex flex-col gap-1 text-xs sm:text-sm text-gray-400">
            <span className="flex items-center gap-1.5">
              <CalendarDays size={13} className="flex-shrink-0 text-gray-500" />
              {formatDateRange(event)}
            </span>
            {location && (
              <span className="flex items-center gap-1.5 min-w-0">
                <MapPin size={13} className="flex-shrink-0 text-gray-500" />
                <span className="truncate">
                  {flag ? `${flag} ` : ''}
                  {location}
                </span>
              </span>
            )}
          </div>

          {/* Beschreibung (optional, kurz) */}
          {event.description && (
            <p className="text-xs text-gray-500 line-clamp-2">{event.description}</p>
          )}

          {/* Links */}
          {(event.ticketUrl || event.registrationUrl || event.sourceUrl) && (
            <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
              {event.ticketUrl && (
                <LinkButton
                  href={event.ticketUrl}
                  icon={<Ticket size={13} />}
                  label="Tickets"
                  primary
                />
              )}
              {event.registrationUrl && (
                <LinkButton
                  href={event.registrationUrl}
                  icon={<ClipboardList size={13} />}
                  label="Anmeldung"
                />
              )}
              {event.sourceUrl && (
                <LinkButton
                  href={event.sourceUrl}
                  icon={<ExternalLink size={13} />}
                  label="Quelle"
                  onClick={
                    onOpenSource
                      ? (e) => {
                          e.preventDefault();
                          onOpenSource(event);
                        }
                      : undefined
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default EventTimelineCard;
